/**
 * Test mode — the concept's `flow/TestPanel.jsx`, walking a REAL person
 * through the graph with `POST /flows/:id/test`.
 *
 * The person is picked from the people Wend can name for this flow (its
 * audience, its entrants, Reach leads), always masked — a name and `a***@bank.in`
 * or `+91 ••••• ••123`, never a full address. The walk is the API's: each step
 * with when it would happen (after waits and quiet hours, in IST), the path it
 * takes, and for a message the rendered preview with that person's values.
 *
 * **It sends nothing**, and says so twice: the contract's `sendsNothing: true`
 * is checked before a walk is drawn, and the panel's subtitle states it. The
 * concept's "send this step to my phone" is deliberately NOT ported — a test
 * send is a send, and this workspace's rule is that the editor never sends.
 */
import type { FlowTestResponse, MaskedPerson } from '@wend/shared';
import { cn } from '@wend/ui';

import { CTRL, HINT, KICK } from './controls';
import { BTN_GHOST, ICON_BTN } from './forms';
import { IcBack, IcClose, IcFlask, IcNext, IcRestart } from './icons';
import { fmtIst } from './model';

export interface TestPanelProps {
  people: readonly MaskedPerson[] | null;
  source: string | null;
  peopleError: string | null;
  personId: string | null;
  setPersonId: (id: string) => void;
  walk: FlowTestResponse | null;
  walking: boolean;
  walkError: string | null;
  idx: number;
  setIdx: (idx: number) => void;
  versionLabel: string;
  onExit: () => void;
}

function personLabel(person: MaskedPerson): string {
  return `${person.name ?? 'No name on file'} · ${person.address ?? 'no address'}`;
}

export function TestPanel({ people, source, peopleError, personId, setPersonId, walk, walking, walkError, idx, setIdx, versionLabel, onExit }: TestPanelProps) {
  const steps = walk?.sendsNothing === true ? walk.steps : [];
  const step = steps[idx];
  const last = idx >= steps.length - 1;

  return (
    <aside
      aria-label="Test this flow"
      data-flow-test=""
      className="absolute inset-x-2 bottom-2 z-[25] flex max-h-[62%] flex-col gap-3 overflow-auto rounded-[14px] border border-line-strong bg-[rgba(25,19,56,.97)] p-3.5 shadow-[-24px_0_50px_-20px_rgba(0,0,0,.75)] backdrop-blur-[10px] motion-safe:animate-pop md:inset-x-auto md:bottom-3 md:left-3 md:top-3 md:max-h-none md:w-[340px]"
    >
      <div className="flex items-center gap-2.5">
        <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] text-white">
          <IcFlask size={15} />
        </span>
        <div>
          <div className="text-[15px] font-semibold leading-tight text-white">Test as a real person</div>
          <div className="text-[12px] text-muted">Sends nothing · walks {versionLabel}</div>
        </div>
        <button type="button" className={cn(ICON_BTN, 'ml-auto')} aria-label="Leave test mode" onClick={onExit}>
          <IcClose size={15} />
        </button>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className={KICK}>Test person</span>
        <select className={CTRL} value={personId ?? ''} onChange={(e) => e.target.value && setPersonId(e.target.value)} disabled={!people || people.length === 0}>
          {(!people || people.length === 0) && <option value="">{people ? 'Nobody to test with yet' : 'Loading people…'}</option>}
          {people?.map((p) => (
            <option key={p.id} value={p.id}>{personLabel(p)}</option>
          ))}
        </select>
        {source && <span className={HINT}>{source}. Addresses are masked.</span>}
        {peopleError && <span className={cn(HINT, 'text-bad')}>{peopleError}</span>}
      </label>

      {walking && <p className={HINT} aria-live="polite">Walking the graph…</p>}
      {walkError && <p className={cn(HINT, 'text-bad')} role="alert">{walkError}</p>}

      {steps.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-0.5 p-0" data-role="test-steps">
          {steps.map((s, i) => (
            <li key={`${s.nodeId ?? 'end'}-${i}`}>
              <button
                type="button"
                onClick={() => setIdx(i)}
                aria-current={i === idx ? 'step' : undefined}
                className={cn(
                  'flex w-full cursor-pointer items-start gap-2.5 rounded-lg border-0 bg-transparent px-2 py-[7px] text-left text-muted hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-brand-b',
                  i === idx && 'bg-brand-a/20 text-white',
                )}
              >
                <span
                  className={cn(
                    'mt-px grid size-5 flex-none place-items-center rounded-full border border-line-strong font-mono text-[10.5px]',
                    i < idx && 'border-transparent bg-brand-b/32 text-white',
                    i === idx && 'border-transparent bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] text-white',
                  )}
                >
                  {i + 1}
                </span>
                <span className="text-[13px] leading-snug">
                  {s.title}
                  <small className="mt-px block font-mono text-[10.5px] text-muted">{s.at ? fmtIst(s.at) : '—'}</small>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}

      {step && (
        <div className="border-t border-line pt-2.5 text-[13px] text-muted" aria-live="polite">
          {step.lines.map((line, i) => (
            <p key={i} className="m-0 mb-1.5">{line}</p>
          ))}
          {step.preview?.channel === 'whatsapp' && (
            <div className="mt-2 rounded-xl border border-good/20 bg-[#0d1418] p-2.5">
              <div className="mb-2 rounded-[4px_12px_12px_12px] border border-good/32 bg-good/14 px-3 py-2.5 text-[13px] leading-normal text-[#E6FFF2]">
                {step.preview.header && <b className="mb-1 block">{step.preview.header}</b>}
                {step.preview.body}
                {step.preview.footer && <span className="mt-1 block text-[11.5px] text-good/70">{step.preview.footer}</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {step.preview.buttons.map((b) => (
                  <span key={b} className="rounded-lg border border-line-strong bg-white/5 px-2.5 py-1 text-[12.5px] text-white">{b}</span>
                ))}
              </div>
            </div>
          )}
          {step.preview?.channel === 'email' && (
            <div className="mt-2 rounded-[10px] border border-info/28 bg-info/7 px-3 py-2.5">
              <div className="mb-1 text-[13px] font-semibold text-white">{step.preview.subject}</div>
              <div className="whitespace-pre-wrap text-[12.5px] text-muted">{step.preview.text}</div>
            </div>
          )}
        </div>
      )}

      {steps.length > 0 && (
        <div className="flex gap-2">
          <button type="button" className={BTN_GHOST} aria-label="Restart test" onClick={() => setIdx(0)}>
            <IcRestart size={14} />
          </button>
          <button type="button" className={BTN_GHOST} aria-disabled={idx === 0 || undefined} onClick={() => idx > 0 && setIdx(idx - 1)}>
            <IcBack size={14} /> Back
          </button>
          <button
            type="button"
            aria-disabled={last || undefined}
            onClick={() => !last && setIdx(idx + 1)}
            className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] px-3 py-2 text-[13px] font-medium text-white aria-disabled:cursor-not-allowed aria-disabled:opacity-45 focus-visible:outline-2 focus-visible:outline-brand-b"
          >
            {last ? 'End of path' : 'Next step'} <IcNext size={14} />
          </button>
        </div>
      )}
      <p className={HINT}>Nothing is sent to anyone in test mode — not to this person, not to you.</p>
    </aside>
  );
}
